/**
 * Shared validation functions for client and server
 */

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254;
}

export function validatePassword(password: string): boolean {
  // Password must be at least 8 characters long
  if (password.length < 8) return false;

  // Must contain at least one uppercase letter
  if (!/[A-Z]/.test(password)) return false;

  // Must contain at least one lowercase letter
  if (!/[a-z]/.test(password)) return false;

  // Must contain at least one number
  if (!/\d/.test(password)) return false;

  // Must contain at least one special character — any non-alphanumeric. The old
  // narrow whitelist excluded the hyphen, so Apple/iCloud Keychain strong
  // passwords (e.g. "kweJ3v-mfgqno-zorbap", whose only symbol is "-") were
  // wrongly rejected. This accepts hyphen, underscore, space, etc.
  if (!/[^A-Za-z0-9]/.test(password)) return false;

  return true;
}

export function validateName(name: string): boolean {
  return name.length >= 1 && name.length <= 100 && /^[a-zA-Z\s'-]+$/.test(name);
}

export function validatePhoneNumber(phone: string): boolean {
  // Accept US phone numbers with or without formatting
  const phoneRegex = /^\+?1?[-.\s]?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;
  return phoneRegex.test(phone);
}

export function validateUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export function sanitizeInput(input: string): string {
  return input.trim().replace(/[<>]/g, '');
}

export function validateClassTitle(title: string): boolean {
  return title.length >= 3 && title.length <= 200;
}

export function validateClassDescription(description: string): boolean {
  return description.length >= 10 && description.length <= 2000;
}

export function validatePrice(price: number): boolean {
  return price >= 0 && price <= 10000 && Number.isFinite(price);
}

export function validateCreatorProfile(profile: any): boolean {
  if (!profile.displayName || typeof profile.displayName !== 'string') return false;
  if (profile.displayName.length < 2 || profile.displayName.length > 100) return false;

  if (profile.bio && typeof profile.bio === 'string' && profile.bio.length > 2000) return false;
  if (
    profile.creatorHeadline &&
    (typeof profile.creatorHeadline !== 'string' || profile.creatorHeadline.length > 120)
  ) {
    return false;
  }

  if (profile.expertise && !Array.isArray(profile.expertise)) return false;

  return true;
}

export function validateCategoryData(category: any): boolean {
  if (!category.name || typeof category.name !== 'string') return false;
  if (category.name.length < 2 || category.name.length > 100) return false;

  if (!category.slug || typeof category.slug !== 'string') return false;
  if (category.slug.length < 2 || category.slug.length > 100) return false;
  if (!/^[a-z0-9-]+$/.test(category.slug)) return false;

  if (
    category.description &&
    typeof category.description === 'string' &&
    category.description.length > 500
  )
    return false;

  return true;
}

export function validateForumContent(content: string): boolean {
  if (typeof content !== 'string') return false;

  // Basic profanity and spam detection (expand as needed)
  const bannedWords = ['spam', 'scam', 'viagra', 'casino'];
  const lowercaseContent = content.toLowerCase();

  for (const word of bannedWords) {
    if (lowercaseContent.includes(word)) return false;
  }

  // Check for excessive links (potential spam)
  const linkCount = (content.match(/https?:\/\//g) || []).length;
  if (linkCount > 3) return false;

  return true;
}
